import type { UIMessage } from "@ai-sdk/react";
import { useAccount } from "@qw/auth";
import type { ChatSessionFragment, DefaultPromptInput } from "@qw/graphql";
import type { MCPPlatform } from "@qw/qw-common";
import { useSetAtom } from "jotai";
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { activeChatSessionIdAtom, completedResponsesAtom } from "../atoms";
import { adaptChatMessagesToUIMessages } from "../chat.helpers";
import type { ExtendedUIMessage, FileResourceLinkHandler, ResourceLinkHandler } from "../chat.types";
import { isChatSessionReadOnly } from "../chat-writability";
import { useChatResourceLinkTracking } from "../hooks/use-chat-resource-link-tracking";
import { useChatScrollControls } from "../hooks/use-chat-scroll-controls";
import { useChatSessionStatusLiveCache } from "../hooks/use-chat-session-status-live-cache";
import { useConversationFreshness } from "../hooks/use-conversation-freshness";
import { useConversationMessages } from "../hooks/use-conversation-messages";
import { type UseMcpChatReturn, useMcpChat } from "../hooks/use-mcp-chat";
import { useTranscriptDimensions } from "../hooks/use-transcript-dimensions";
import type { ServerHistoryState } from "../server-history-state";
import { ChatToolMetadataProvider } from "./chat-tool-metadata-provider";

/**
 * Render state of the server-authoritative history source, surfaced so `ChatContent` can show a
 * loading spinner during the cold whole-tree read and the right non-empty state on failure — instead
 * of flashing the empty-conversation component (brief 015). Aliased to `ServerHistoryState["status"]`
 * so there is one status contract, not a parallel copy: `ready` is a loaded tree (including a
 * legitimately empty one); `error` is a (retryable) cold query failure; `unsupported` is the
 * `UnsupportedRuntime` capability seam — a PERMANENT condition, rendered non-retryable and kept
 * distinct from `error` so it never shows a Retry that can't succeed. Never silently empty.
 */
export type ChatHistoryStatus = ServerHistoryState["status"];

export interface ChatContextValue extends UseMcpChatReturn, ReturnType<typeof useChatScrollControls> {
  apiUrl: string;
  historyStatus: ChatHistoryStatus;
  /** Re-fetch the persisted history tree — wired to the `ChatContent` history-load error retry. */
  retryHistory: () => Promise<void>;
  chatSession: ChatSessionFragment;
  fileResourceLinkHandler?: FileResourceLinkHandler;
  isReadOnly: boolean;
  resourceLinkHandlers?: Record<string, ResourceLinkHandler>;
  /** Subscribe to stream completion events. Returns unsubscribe function. */
  onStreamComplete: (callback: () => void) => () => void;
  /**
   * Marks a resource URI as seen and returns whether onFirstSeen should be called.
   * Returns true only if: the URI hasn't been seen before AND status is streaming.
   * This state persists across ChatProvider unmount/remount cycles via Jotai atom.
   */
  markResourceSeen: (uri: string) => boolean;
}

export const ChatContext = createContext<ChatContextValue | null>(null);

export interface ToolFilter {
  whitelist?: string[] | null;
  blacklist?: string[] | null;
}

export interface ChatProviderProps {
  apiUrl?: string;
  chatSession: ChatSessionFragment;
  fileResourceLinkHandler?: FileResourceLinkHandler;
  initialMessages?: ExtendedUIMessage[];
  onError?: (error: Error) => void;
  onFinish?: (message: UIMessage) => void;
  onResponse?: (response: UIMessage) => void;
  promptInput?: DefaultPromptInput;
  resourceLinkHandlers?: Record<string, ResourceLinkHandler>;
  systemPrompt?: string;
  platform?: MCPPlatform;
  toolFilter?: ToolFilter | null;
  toolFilterStorageKey?: string;
  liveStreamEnabled?: boolean;
}

export const ChatProvider: FC<PropsWithChildren<ChatProviderProps>> = ({
  apiUrl = location.origin,
  chatSession,
  children,
  fileResourceLinkHandler,
  initialMessages: customInitialMessages,
  onError,
  onFinish,
  onResponse,
  platform,
  promptInput,
  resourceLinkHandlers,
  systemPrompt: customSystemPrompt,
  toolFilter,
  toolFilterStorageKey,
  liveStreamEnabled = true,
}) => {
  const { user: currentUser } = useAccount();
  const setActiveChatSessionId = useSetAtom(activeChatSessionIdAtom);
  const setCompletedResponses = useSetAtom(completedResponsesAtom);

  // Mirror server-pushed chat session status changes into Apollo's
  // cache. Every consumer reading the `ChatSession` entity (any query,
  // fragment-derived hook, etc.) automatically sees the live `status`
  // field via Apollo's cache broadcasts — no per-consumer refetch
  // wiring needed.
  //
  // Scoped to the chat session's owner so internal users viewing
  // another user's session route via the chat session's userId (the
  // server filter accepts either `event.userId === user.id` or an
  // internal-subscription user).
  useChatSessionStatusLiveCache({ userId: chatSession.userId });

  // Stream completion listeners - components can subscribe to be notified when streaming ends
  const streamCompleteListenersRef = useRef<Set<() => void>>(new Set());

  const onStreamComplete = useCallback((callback: () => void) => {
    streamCompleteListenersRef.current.add(callback);
    return () => {
      streamCompleteListenersRef.current.delete(callback);
    };
  }, []);

  // Track when this chat session becomes active/inactive for notification purposes
  useEffect(() => {
    setActiveChatSessionId(chatSession.id);
    return () => {
      setActiveChatSessionId((currentId) => (currentId === chatSession.id ? null : currentId));
    };
  }, [chatSession.id, setActiveChatSessionId]);

  const systemPrompt = customSystemPrompt || "";
  const isReadOnly = isChatSessionReadOnly(chatSession, currentUser?.id);

  const initialMessages = useMemo(
    () => customInitialMessages || adaptChatMessagesToUIMessages(chatSession.messages),
    [customInitialMessages, chatSession.messages]
  );

  // The persisted render base, from the server-owned whole-tree `conversationMessages`
  // query (root + all child sessions, ordered + child-`user`-filtered server-side). One
  // server-owned source — no client fan-out, no root-refresh gap, single history owner.
  // `refetch` is driven directly by the SDK's stream-finished callback. Server-authoritative
  // render is the only path now (the `server-authoritative-chat-history-enabled` flag was
  // removed after full rollout — brief 015), so the query always runs.
  const serverHistorySource = useConversationMessages(chatSession.id);
  const serverState = serverHistorySource.state;

  // The persisted render base: the ready message tree, or an empty base until the whole-tree query
  // resolves. `historyStatus` (below) drives the loading/error UI so a not-yet-ready base is shown
  // as loading/error, never as an empty conversation.
  const serverHistory = serverState.status === "ready" ? serverState.messages : [];

  // The render surfaces the source's own status discriminant directly (no parallel mapping): `ready`
  // renders content, `loading` a spinner, `error` a retryable banner, `unsupported` a distinct
  // non-retryable banner (permanent capability seam). See `ChatHistoryStatus` / `ChatContent`.
  const historyStatus: ChatHistoryStatus = serverState.status;

  // Reconcile persisted history after every real stream outcome (success, abort,
  // disconnect, or error). `useMcpChat` fires this from the AI SDK's onFinish callback.
  const handleStreamSettled = useCallback(() => serverHistorySource.refetch(), [serverHistorySource.refetch]);

  // Retry a failed cold history load — wired to the `ChatContent` history-error state's Retry action.
  // Swallow rejection (mirrors the scheduled/on-finish refetch paths) so a failed retry can't surface
  // as an unhandled promise rejection; the failure is reflected in the next `historyStatus`.
  const retryHistory = useCallback(
    () => serverHistorySource.refetch().catch(() => undefined),
    [serverHistorySource.refetch]
  );

  // Wrap onFinish to mark the response as completed for notifications.
  const handleFinish = useCallback(
    (message: UIMessage) => {
      onFinish?.(message);

      // Add to completed responses for notification (the ChatNotificationListener will handle it)
      setCompletedResponses((responses) => [...responses, { sessionId: chatSession.id, messageId: message.id }]);

      // Notify all stream completion listeners
      streamCompleteListenersRef.current.forEach((listener) => listener());
    },
    [onFinish, chatSession.id, setCompletedResponses]
  );

  // `ensureAttached` is a controller-only imperative command — destructure it out so it is NOT
  // spread into the context `value` below (it must not become part of every `useChat()` consumer's
  // public API).
  const { ensureAttached, ...mcpChat } = useMcpChat({
    apiUrl,
    isReadOnly,
    sessionId: chatSession.id,
    systemPrompt,
    onError,
    onFinish: handleFinish,
    onStreamSettled: handleStreamSettled,
    onResponse,
    platform,
    promptInput,
    toolFilter,
    toolFilterStorageKey,
    liveStreamEnabled,
    serverHistory,
  });

  // One freshness-detection seam (brief 014 Phase 5): the tab-return and WS-reconnect edges fan out
  // to both leaves — refetch persisted history (server-history path) and re-attach the live stream —
  // replacing the two separate visibility listeners + the WS-reconnect effect that used to live in
  // `useConversationMessages` and `useMcpChat`.
  useConversationFreshness({
    enabled: true,
    refetchHistory: serverHistorySource.scheduleRefetch,
    ensureAttached,
  });

  // Report how large the rendered transcript has grown to the host's telemetry adapter, if it
  // mounted one. Numbers only, and the sink is the host's — `@qw/chat` takes no analytics
  // dependency for this (B-2020).
  useTranscriptDimensions(chatSession.id, liveStreamEnabled ? mcpChat.messages : []);

  const chatScrollControls = useChatScrollControls({ messages: mcpChat.messages });

  // TODO: This feels like a pretty fragile and hacky way to do this. We should think through a better way.
  const resourceTrackingInitialMessages =
    liveStreamEnabled && serverState.status === "ready" ? serverHistory : initialMessages;
  const { markResourceSeen } = useChatResourceLinkTracking({
    sessionId: chatSession.id,
    initialMessages: resourceTrackingInitialMessages,
    status: mcpChat.status,
  });

  useEffect(() => {
    chatScrollControls.resumeAutoScroll();
  }, [chatSession.id]);

  const value = useMemo(
    () => ({
      ...mcpChat,

      // Scroll controls
      ...chatScrollControls,

      // API URL
      apiUrl,

      // Server-history loading/error state for the not-ready render UI
      historyStatus,
      retryHistory,

      // Chat session
      chatSession,
      isReadOnly,

      // Built-in file link overrides
      fileResourceLinkHandler,

      // Resource link handlers
      resourceLinkHandlers,

      // Stream completion subscription
      onStreamComplete,

      // Resource tracking for onFirstSeen
      markResourceSeen,
    }),
    [
      mcpChat,
      apiUrl,
      historyStatus,
      retryHistory,
      chatSession,
      fileResourceLinkHandler,
      isReadOnly,
      resourceLinkHandlers,
      chatScrollControls,
      onStreamComplete,
      markResourceSeen,
    ]
  );

  // The subtree is always rendered — never gated behind a provider-owned spinner. While the cold
  // whole-tree read is in flight `ChatContent` shows its own loading state (driven by `historyStatus`)
  // and preserves child state (draft input, focus) rather than unmounting/remounting the subtree.
  //
  // `ChatToolMetadataProvider` sits inside so the transcript's tool invocations share ONE metadata
  // fetch and one stable lookup map, instead of each running its own query and list scan.
  return (
    <ChatContext.Provider value={value}>
      <ChatToolMetadataProvider>{children}</ChatToolMetadataProvider>
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }

  return context;
};
